<script
  setup
  lang="ts"
  generic="
    TForm extends {
      setFieldValue: (...args: any[]) => void
      useStore: <TSelected>(
        selector: (state: { values: Record<string, unknown> }) => TSelected
      ) => Ref<TSelected>
    }
  "
>
  import { computed, ref, watch, type Ref } from 'vue'
  import {
    ActionCard,
    ActionCardLeadIcon,
    ActionCardTitle,
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Toggle
  } from '@gat-ship/vue-components'
  import { useAccessRoles } from '../composables/useAccessRoles'
  import {
    IconChevronRight,
    IconEye,
    IconEyeOff,
    IconAccessRole
  } from '@gat-ship/icons'
  import type { FieldVisibilityOption } from '@gat-ship/types'

  const props = defineProps<{
    form: TForm
  }>()

  const visibilityPopoverOpen = ref(false)
  const localVisibleRoleIds = ref<string[]>([])
  const localVisibilityOption = ref<FieldVisibilityOption | undefined>(
    undefined
  )
  const step = ref(0)

  const dataSource = useAccessRoles()!

  const roleItems = computed(() =>
    dataSource.accessRoles.value.map((r) => ({ title: r.name, value: r.id }))
  )

  const visibilityOption = props.form.useStore(
    (s) => s.values.visibilityOption as FieldVisibilityOption | undefined
  )

  const visibleRoleIds = props.form.useStore(
    (s) => s.values.visibleForRoleIds as string[] | undefined
  )

  const editableRoleIds = props.form.useStore(
    (s) => s.values.editableForRoleIds as string[] | undefined
  )

  const editableOption = props.form.useStore(
    (s) => s.values.editableOption as string | undefined
  )

  watch(visibilityPopoverOpen, (open) => {
    if (open) {
      localVisibleRoleIds.value = [...(visibleRoleIds.value ?? [])]
      localVisibilityOption.value = visibilityOption.value ?? undefined
      step.value = 0
    }
  })

  function toggleVisibleRole(roleId: string, pressed: boolean) {
    const current = localVisibleRoleIds.value
    localVisibleRoleIds.value = pressed
      ? [...new Set([...current, roleId])]
      : current.filter((id) => id !== roleId)
  }

  const visibleRoleCount = computed(() => visibleRoleIds.value?.length ?? 0)

  function pruneEditableRoles(allowedRoleIds: string[]) {
    if (editableOption.value !== 'editable-for-role') return
    const pruned = (editableRoleIds.value ?? []).filter((id) =>
      allowedRoleIds.includes(id)
    )
    props.form.setFieldValue('editableForRoleIds', pruned)
    if (pruned.length === 0) {
      props.form.setFieldValue('editableOption', 'editable')
    }
  }

  function handleSave() {
    props.form.setFieldValue('visibleForRoleIds', localVisibleRoleIds.value)
    props.form.setFieldValue('visibilityOption', localVisibilityOption.value)
    pruneEditableRoles(localVisibleRoleIds.value)
    visibilityPopoverOpen.value = false
  }

  function handleVisibilityOptionChange(value: FieldVisibilityOption) {
    if (value === 'visible' || value === 'hidden') {
      localVisibilityOption.value = value
      localVisibleRoleIds.value = []
      handleSave()
    } else if (value === 'visible-for-role') {
      localVisibilityOption.value = value
      step.value = 1
    }
  }

  const isSaveDisabled = computed(() => {
    if (localVisibilityOption.value === 'visible-for-role') {
      return localVisibleRoleIds.value.length === 0
    }
    return localVisibilityOption.value === undefined
  })

  const visibilityIcon = computed(() => {
    if (visibilityOption.value === 'visible') {
      return IconEye
    }
    if (visibilityOption.value === 'visible-for-role') {
      return IconAccessRole
    }
    return IconEyeOff
  })
</script>

<template>
  <Popover v-model:open="visibilityPopoverOpen" :modal="true">
    <PopoverTrigger as-child>
      <ActionCard>
        <div class="flex w-full flex-col gap-4">
          <div class="flex w-full items-center gap-2">
            <ActionCardLeadIcon :icon="visibilityIcon" />
            <div class="flex w-full items-center">
              <div>
                <ActionCardTitle> Visibility</ActionCardTitle>
              </div>
              <div class="text-muted-foreground ml-auto text-sm font-normal">
                <span v-if="visibilityOption === 'visible'"> All Users</span>
                <span v-else-if="visibilityOption === 'visible-for-role'">
                  {{ visibleRoleCount }} roles
                </span>
                <span v-else> Hidden </span>
              </div>
            </div>
          </div>
        </div>
      </ActionCard>
    </PopoverTrigger>
    <PopoverContent
      class="flex w-[var(--reka-popover-trigger-width)] flex-col gap-5"
    >
      <div class="flex flex-col gap-6">
        <div v-if="step === 0" class="flex flex-col gap-2">
          <p class="mb-2 text-sm font-normal">
            Who should be able to see this field?
          </p>
          <div
            class="divide-border border-border flex flex-col divide-y border-t"
          >
            <button
              v-if="visibilityOption === 'visible-for-role'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleVisibilityOptionChange('visible-for-role')"
            >
              <IconAccessRole class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">Change role selection</div>
                <div class="text-muted-foreground text-xs font-normal">
                  Change the roles that can see this field.
                </div>
              </div>
              <IconChevronRight class="text-muted-foreground ml-auto size-4" />
            </button>
            <button
              v-if="visibilityOption !== 'visible-for-role'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleVisibilityOptionChange('visible-for-role')"
            >
              <IconAccessRole class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">Users with specific roles</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field will be visible to users with the selected roles.
                </div>
              </div>
              <IconChevronRight class="text-muted-foreground ml-auto size-4" />
            </button>
            <button
              v-if="visibilityOption !== 'visible'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleVisibilityOptionChange('visible')"
            >
              <IconEye class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">All users</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field will be visible to all users in the system.
                </div>
              </div>
            </button>

            <button
              v-if="visibilityOption !== 'hidden'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleVisibilityOptionChange('hidden')"
            >
              <IconEyeOff class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">No users (hidden)</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field will be hidden from all users.
                </div>
              </div>
            </button>
          </div>
        </div>
        <template v-if="step === 1">
          <div class="flex flex-col gap-2">
            <span class="mb-2 text-sm font-normal"
              >Who should be able to see this field?</span
            >

            <div class="flex flex-wrap gap-2">
              <Toggle
                v-for="role in roleItems"
                :key="role.value"
                variant="default"
                size="sm"
                :model-value="localVisibleRoleIds.includes(role.value)"
                class="bg-secondary hover:bg-secondary/80 rounded-full pr-3 text-sm font-normal"
                @update:model-value="
                  (pressed: boolean) => toggleVisibleRole(role.value, pressed)
                "
              >
                <IconAccessRole class="size-4" />
                {{ role.title }}
              </Toggle>
              <div
                v-if="roleItems.length === 0"
                class="text-muted-foreground text-xs italic"
              >
                No roles available.
              </div>
            </div>
          </div>
        </template>
        <div v-if="step === 1" class="flex items-center gap-2">
          <p class="text-muted-foreground text-xs font-normal">
            Select the roles that should be able to see this field. You may
            select 1 or more roles.
          </p>
          <Button class="ml-auto" variant="ghost" size="sm" @click="step = 0">
            Back
          </Button>
          <Button
            variant="default"
            size="sm"
            :disabled="isSaveDisabled"
            @click="handleSave"
          >
            Confirm
          </Button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
